import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchContactCallPrep } from '../api/endpoints.js';

/**
 * "Prep for next call": the latest carry-forward AI note for a contact plus a
 * compact attempt history. Shown on the contact page and in the agent workspace
 * while a call with that contact is active.
 */
export function CallPrepCard({ contactId, compact = false }: { contactId: string; compact?: boolean }) {
  const { t } = useTranslation();
  const prepQ = useQuery({
    queryKey: ['contact-call-prep', contactId],
    queryFn: () => fetchContactCallPrep(contactId),
    enabled: Boolean(contactId),
  });

  const prep = prepQ.data;
  if (!prep || (prep.attempt_count === 0 && !prep.prep_note)) return null;

  const attempts = compact ? prep.attempts.slice(0, 3) : prep.attempts;

  return (
    <section className="card call-prep">
      <h3 className="card__title">
        {t('callAi.prepTitle')}
        <span className="badge">{t('callAi.attempts', { count: prep.attempt_count })}</span>
      </h3>
      {prep.prep_note ? (
        <p className="call-prep__note">{prep.prep_note}</p>
      ) : (
        <p className="muted">{t('callAi.noPrepNote')}</p>
      )}
      {attempts.length > 0 && (
        <ul className="call-prep__attempts">
          {attempts.map((a) => (
            <li key={a.conversation_id}>
              <Link to={`/conversations/${a.conversation_id}`}>
                {a.started_at ? new Date(a.started_at).toLocaleString() : '—'}
              </Link>{' '}
              <span className="badge">
                {(a.disposition ?? t('callAi.noDisposition')).replace(/_/g, ' ')}
                {a.disposition_source === 'ai' ? ' · AI' : ''}
              </span>
              {a.summary && !compact && <span className="muted"> {a.summary}</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
